import { useId } from 'react';
import type { DailyWorkDto } from '../../api/attendance';
import type { MyWageSpanDto } from '../../api/hr-my-wage';
import './AttendanceCharts.css';

/**
 * 근태 차트 두 벌 (2026-09-03 · 개인 근태 P3-3).
 *
 * ## ★★★ 계열색은 `--primary` 를 쓰지 않는다
 *
 * 테마가 다섯이라 `--primary` 로 그리면 **같은 차트가 테마마다 다른 뜻으로 읽힌다**
 * (그린 테마에서 초록 막대는 「좋다」로, 핑크 테마에서는 「위험」으로 읽힌다).
 * 그래서 테마와 무관한 **고정 팔레트**를 `AttendanceCharts.css` 에 둔다.
 * 값은 눈으로 고르지 않고 **검증기로 골랐다** — 색각 분리 ΔE 22.2(deutan) ·
 * 정상시야 25.0 · 표면 대비 3:1↑ 로 전 항목 통과.
 *
 * ## ★★ 세 번째 계열은 «색» 이 아니라 «빗금» 이다
 *
 * 「미신청 잔여」는 아직 확정되지 않은 시간이라, 색을 하나 더 쓰는 것보다 무늬가 옳다.
 * 색각·흑백 인쇄에서도 남고, 「덜 단단한 것」이라는 뜻을 무늬가 함께 진다.
 *
 * ## ★★ 0 을 «낮은 막대» 로 그리지 않는다
 *
 * 휴가는 0시간이다. 낮은 막대를 세우면 「조금 일했다」로 읽힌다 —
 * 일하지 않은 날은 **막대를 세우지 않고 축 아래에 낱말**로 적는다.
 */

/*
 * hardcoded-korean-exception: 근태 도메인은 아직 한국어 고정이다 — 셸 · 관리 화면 열아홉이
 * 모두 그렇고, 이 파일만 i18n 을 들이면 같은 화면 안에서 두 체계가 섞인다. 도메인을 통째로
 * 옮길 때 여기 낱말(지각 · 휴가 · 간주 · 소정 N시간 · 연장 · 야간)도 함께 간다.
 * ★ 읽어 주는 글(aria-label)까지 여기 있으므로, 옮길 때 **그림 설명을 빠뜨리지 말 것**.
 */
const CHART_W = 720;

/** 분 → `1.5h` 꼴. 0 은 `0h`. */
function hoursOf(minutes: number): string {
  return `${(minutes / 60).toFixed(1)}h`;
}

/** `YYYY-MM-DD` → 일(숫자). 깨졌으면 0. */
function dayOf(date: string): number {
  const n = Number(date.slice(8, 10));
  return Number.isFinite(n) ? n : 0;
}

export interface MonthlyWorkChartProps {
  days: readonly DailyWorkDto[];
  /** 소정 근로(분). 기준선을 여기 긋는다. */
  standardMinutes?: number;
}

/**
 * 이번 달 근무 — 일별로 인정 근로 위에 승인 연장을 쌓고, 미신청 잔여는 빗금으로 얹는다.
 *
 * ★ 세 값 모두 서버가 이미 준다(`DailyWorkDto`) — 새 API 가 없다.
 * ★ 지각은 **선택 칸**(`late`)이다. 옛 서버가 안 주면 표시가 없을 뿐, 없는 것을
 *   「지각이 아니었다」로 그리지 않는다.
 */
export function MonthlyWorkChart({ days, standardMinutes = 480 }: MonthlyWorkChartProps) {
  const patternId = useId();
  if (days.length === 0) return null;

  const L = 34, R = 8, T = 14, B = 32;
  const H = 200;
  const pw = CHART_W - L - R;
  const ph = H - T - B;

  const peak = days.reduce(
    (m, d) => Math.max(m, d.creditedMinutes + Math.max(d.overtimeMinutes, d.unclaimedMinutes)),
    standardMinutes,
  );
  // 눈금을 2시간 단위로 올려 잡는다 — 꼭대기가 천장에 붙으면 넘친 것처럼 보인다.
  const max = Math.ceil((peak * 1.08) / 120) * 120;
  const y = (m: number) => T + ph - (m / max) * ph;

  const step = pw / days.length;
  const bw = Math.min(16, Math.max(4, step - 5));
  const ticks: number[] = [];
  for (let m = 0; m <= max; m += 120) ticks.push(m);

  const lastDay = dayOf(days[days.length - 1]?.date ?? '');

  return (
    <svg
      className="attchart"
      viewBox={`0 0 ${CHART_W} ${H}`}
      role="img"
      aria-label={
        `일별 근무 시간. 인정 근로 위에 승인 연장을 쌓고, 신청하지 않은 잔여 시간은 빗금으로 표시했다. ` +
        `가로 기준선은 소정 ${Math.round(standardMinutes / 60)}시간.`
      }
    >
      <defs>
        <pattern id={patternId} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="5" height="5" className="attchart__hatchbg" />
          <rect width="2" height="5" className="attchart__hatchink" />
        </pattern>
      </defs>

      {ticks.map((m) => {
        const isBase = m === standardMinutes;
        return (
          <g key={m}>
            <line
              x1={L} x2={CHART_W - R} y1={y(m)} y2={y(m)}
              className={isBase ? 'attchart__baseline' : 'attchart__grid'}
            />
            <text x={L - 6} y={y(m) + 3.5} textAnchor="end" className="attchart__tick">{m / 60}h</text>
          </g>
        );
      })}
      <text x={CHART_W - R} y={y(standardMinutes) - 6} textAnchor="end" className="attchart__baselab">
        소정 {Math.round(standardMinutes / 60)}시간
      </text>

      {days.map((d, i) => {
        const day = dayOf(d.date);
        const x = L + i * step + (step - bw) / 2;
        const cx = x + bw / 2;
        const cr = d.creditedMinutes;
        const ot = d.overtimeMinutes;
        const un = d.unclaimedMinutes;
        // ★ 일하지 않은 날은 막대가 아니라 낱말이다 — 0 을 높이로 그리지 않는다.
        const word = d.status === 'LEAVE' ? '휴가' : d.status === 'DEEMED' && cr === 0 ? '간주' : null;
        const showDate = day % 4 === 1 || day === lastDay;

        return (
          <g key={d.date}>
            {cr > 0 && (
              <rect x={x} y={y(cr)} width={bw} height={y(0) - y(cr)} rx={3} className="attchart__credited">
                <title>
                  {day}일 · 인정 근로 {hoursOf(cr)}
                  {ot > 0 ? ` · 승인 연장 ${hoursOf(ot)}` : ''}
                  {un > 0 ? ` · 미신청 ${hoursOf(un)}` : ''}
                  {d.late ? ' · 지각' : ''}
                </title>
              </rect>
            )}
            {/* 쌓은 조각 사이를 2px 벌린다 — 같은 굵기의 색 둘이 붙으면 한 덩어리로 읽힌다. */}
            {ot > 0 && (
              <rect
                x={x} y={y(cr + ot)} width={bw}
                height={Math.max(y(cr) - y(cr + ot) - 2, 2)} rx={3} className="attchart__overtime"
              >
                <title>{day}일 · 승인 연장 {hoursOf(ot)}</title>
              </rect>
            )}
            {un > 0 && (
              <rect
                x={x} y={y(cr + un)} width={bw}
                height={Math.max(y(cr) - y(cr + un) - 2, 2)} rx={3}
                fill={`url(#${patternId})`} className="attchart__unclaimed"
              >
                <title>{day}일 · 미신청 잔여 {hoursOf(un)} — 신청하면 인정될 수 있습니다</title>
              </rect>
            )}
            {d.late && (
              <text x={cx} y={y(0) + 20} textAnchor="middle" className="attchart__late">지각</text>
            )}
            {!d.late && word && (
              <text x={cx} y={y(0) + 20} textAnchor="middle" className="attchart__word">{word}</text>
            )}
            {showDate && (
              <text x={cx} y={y(0) + 11} textAnchor="middle" className="attchart__day">{day}</text>
            )}
          </g>
        );
      })}

      <line x1={L} x2={CHART_W - R} y1={y(0)} y2={y(0)} className="attchart__axis" />
    </svg>
  );
}

export interface PremiumOverlapChartProps {
  /** 그릴 하루 — 가산 내역 중 «야간에 걸친» 한 건. */
  span: MyWageSpanDto;
  /** 야간 창 `22:00-06:00`. */
  nightWindow: string;
}

/** `HH:MM` → 분. 깨졌으면 null. */
function toMin(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]), mi = Number(m[2]);
  if (h < 0 || h > 24 || mi < 0 || mi > 59) return null;
  return h * 60 + mi;
}

/**
 * 겹침 띠 — 「넷을 더하지 마세요」를 **글 대신 그림**이 말한다.
 *
 * 하루를 시각 축으로 펼쳐, 연장 띠 위에 야간 띠를 **겹쳐서** 놓는다.
 * 22시부터 두 띠가 같은 자리를 덮는 것이 보이면 더하면 안 되는 이유가 설명 없이 전해진다.
 *
 * ★ 야간 시작이 span 밖이면(겹치는 구간이 없으면) **그리지 않는다** — 겹치지 않는데
 *   겹친다고 그리면 그림이 거짓말을 한다.
 */
export function PremiumOverlapChart({ span, nightWindow }: PremiumOverlapChartProps) {
  const start = toMin(span.start);
  const end = toMin(span.end);
  const nightStart = toMin(nightWindow.split('-')[0] ?? '');
  if (start === null || end === null || nightStart === null) return null;
  if (nightStart <= start || nightStart >= end) return null;

  const H = 132, L = 58, R = 14;
  const pw = CHART_W - L - R;
  // 축은 시작 한 시간 전부터 끝난 시각의 다음 정시까지 — 띠가 축 끝에 붙지 않게.
  const t0 = Math.floor(start / 60) * 60 - 60;
  // 끝난 시각의 다음 정시까지 — 그 여백에 값(3.5h)이 선다.
  const t1 = Math.ceil(end / 60) * 60;
  const x = (m: number) => L + ((m - t0) / (t1 - t0)) * pw;

  const hours: number[] = [];
  for (let h = t0 / 60; h <= t1 / 60; h += 1) hours.push(h);
  const nightMinutes = end - nightStart;

  return (
    <svg
      className="attchart"
      viewBox={`0 0 ${CHART_W} ${H}`}
      role="img"
      aria-label={
        `${span.start}부터 ${span.end}까지 연장 근로 ${hoursOf(end - start)}. ` +
        `그중 ${nightWindow.split('-')[0]} 이후 ${hoursOf(nightMinutes)}는 야간과 겹친다. ` +
        `그래서 연장과 야간을 더하면 겹친 구간을 두 번 세게 된다.`
      }
    >
      {hours.map((h) => (
        <g key={h}>
          <line x1={x(h * 60)} x2={x(h * 60)} y1={20} y2={92} className="attchart__grid" />
          <text x={x(h * 60)} y={14} textAnchor="middle" className="attchart__tick">
            {String(h % 24).padStart(2, '0')}:00
          </text>
        </g>
      ))}

      <text x={L - 10} y={46} textAnchor="end" className="attchart__rowlab">연장</text>
      <rect x={x(start)} y={32} width={x(end) - x(start)} height={22} rx={4} className="attchart__credited">
        <title>{span.start}–{span.end} 연장 {hoursOf(end - start)}</title>
      </rect>
      {/* ★ 값은 **띠 밖**에 적는다 — 띠 색 위에서는 어느 모드에서도 대비가 안 나온다
             (흰 글자 3.2:1 · 감색 글자 3.5:1, 둘 다 4.5 미달). 잉크 토큰을 쓰면 늘 읽힌다. */}
      <text x={x(end) + 8} y={47} className="attchart__inbar">{hoursOf(end - start)}</text>

      <text x={L - 10} y={80} textAnchor="end" className="attchart__rowlab">야간</text>
      {/* 표면색 테두리로 두 띠를 가른다 — 겹친 자리에서 색이 붙어 한 덩어리로 읽히지 않게. */}
      <rect
        x={x(nightStart)} y={66} width={x(end) - x(nightStart)} height={22} rx={4}
        className="attchart__overtime attchart__overlap"
      >
        <title>{nightWindow.split('-')[0]}–{span.end} 야간 {hoursOf(nightMinutes)} — 연장과 겹친다</title>
      </rect>
      <text x={x(end) + 8} y={81} className="attchart__inbar">{hoursOf(nightMinutes)}</text>

      <line x1={x(nightStart)} x2={x(nightStart)} y1={28} y2={96} className="attchart__cut" />
      <text x={x(nightStart) + 8} y={110} className="attchart__cutlab">
        {nightWindow.split('-')[0]}부터 겹친다
      </text>
      <text x={2} y={110} className="attchart__sum">
        {hoursOf(end - start)} + {hoursOf(nightMinutes)} ≠ {hoursOf(end - start + nightMinutes)}
      </text>
    </svg>
  );
}
